import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { DocumentService } from '../../document/document.service';
import { DocumentModel } from '../../document/document.model';
import { FiDateFormatOptions } from '@fi-shared/utils';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { ObjectTypes } from '@fi-shared/types';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { takeUntil } from 'rxjs/operators';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';

@Component({
    selector: 'fi-system-notifications-autosaved',
    templateUrl: './autosaved.component.html',
    styleUrls: ['./autosaved.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-system-notifications-autosaved' },
    standalone: false,
})
export class SystemNotificationsAutosavedComponent
    extends FiInfiniteScrollDataSource<DocumentModel>
    implements OnInit, OnDestroy
{
    defaultSortColumnName: string = 'ChangedOn';
    FiStateNames = FiStateNames;
    itemsListType = FiItemsListType.SystemNotificationsAutosaved;
    objectType = ObjectTypes.Document;

    constructor(
        private appTextService: AppTextService,
        private documentService: DocumentService,
        private notificationHelper: FiNotificationHelperService,
        private fiDialogsService: FiDialogsService,
        private fiFormatDateService: FiFormatDateService,
        injector: Injector,
    ) {
        super(injector, {});
    }

    ngOnInit() {
        this.documentService.autoSavedDocumentRemoved$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((data) => {
            const items = this.items();
            const documentToRemove = items.find((item) => item.OriginalId === data.OriginalId);
            if (documentToRemove) {
                this.removeData([documentToRemove]);
            }
            this.documentService.onDraftDocumentsCountChange.next(items.length);
        });

        this.reloadData();
    }

    isDocumentNotVisited(document: DocumentModel): boolean {
        return this.notificationHelper.isNotVisited(document);
    }

    getListData() {
        return new Promise<DocumentModel[]>((resolve, reject) => {
            this.documentService.getDraftDocuments().then((documents) => {
                const listItems: DocumentModel[] = [];
                documents.forEach((data) => {
                    const document = new DocumentModel(data, true, this.injector);
                    document.PreviewFileName = null;
                    if (document.DraftNumber > 0) {
                        document.Name = this.appTextService.get('common.new_page') + ' (' + document.DraftNumber + ')';
                    }
                    if (!this.documentService.IsDocumentInDeleteProgress(document.Id)) {
                        listItems.push(document);
                    }
                });

                resolve(listItems);
            }, reject);
        });
    }

    getChangedOnCellValue(checklist: DocumentModel): string {
        return this.fiFormatDateService.formatDate(new Date(checklist.ChangedOn), FiDateFormatOptions);
    }

    confirmDelete() {
        this.fiDialogsService
            .confirm({
                messageText: this.appTextService.get('ccmDraftsList.confirm_delete_document'),
                headerText: this.appTextService.get('common.warning'),
                yesButtonText: this.appTextService.get('common.delete'),
            })
            .onYesButtonClick(() => this.removeDraftDocuments());
    }

    private removeDraftDocuments() {
        const documentsToDelete = this.selectedItems().slice();
        this.documentService.deleteDocuments(documentsToDelete.map((document) => document.Id)).then(() => {
            this.removeData(documentsToDelete);
            this.documentService.onDraftDocumentsCountChange.next(this.items.length);
        });
    }
}
